import { Component, OnInit, Inject } from "@angular/core";
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { Store } from "@ngrx/store";
import { ChangeBannerAction } from "../banners.actions";
import { Image } from "../../../shared/image-uploader/image.interface";
import { ImageService } from "../../../shared/image-uploader/image.service";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { BannerEditDialogData, ChangeBannerObject } from "../banners.interface";
import { ShowOnSubmitAttemptErrorStateMatcher } from "../../../shared/error-state-matcher";

@Component({
  selector: "app-banner-edit",
  templateUrl: "./banner-edit.component.html",
  styleUrls: ["./banner-edit.component.scss"],
  standalone: false,
})
export class BannerEditComponent implements OnInit {
  file;
  public form: UntypedFormGroup;
  saving = false;
  private readonly MAX_PREVIEW_WIDTH = 500;
  editMode = true;
  previewUrl: string | null = null;
  errorStateMatcher = new ShowOnSubmitAttemptErrorStateMatcher();

  constructor(
    private store: Store,
    public imageService: ImageService,
    private dialogRef: MatDialogRef<BannerEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data: BannerEditDialogData,
  ) {}

  ngOnInit(): void {
    this.fillInitialForm();
  }

  fillInitialForm = (): void => {
    const bannerData = this.data?.banner;
    this.previewUrl = bannerData?.imageUrl || null;

    this.form = new UntypedFormGroup({
      addonId: new UntypedFormControl(bannerData?.addonId ?? "", (c) =>
        Validators.required(c),
      ),
      file: new UntypedFormControl(""),
      name: new UntypedFormControl(bannerData?.name || "", (c) =>
        Validators.required(c),
      ),
      url: new UntypedFormControl(bannerData?.url || "", (c) =>
        Validators.required(c),
      ),
      utm: new UntypedFormControl(bannerData?.utm || "gsignature", (c) =>
        Validators.required(c),
      ),
      imageWidth: new UntypedFormControl(bannerData?.imageWidth || 400, (c) =>
        Validators.min(0)(c),
      ),
      imageHeight: new UntypedFormControl(
        bannerData?.imageHeight || null,
        (c) => Validators.min(0)(c),
      ),
      originalImageWidth: new UntypedFormControl(
        bannerData?.originalImageWidth || null,
        (c) => Validators.min(0)(c),
      ),
      originalImageHeight: new UntypedFormControl(
        bannerData?.originalImageHeight || null,
        (c) => Validators.min(0)(c),
      ),
    });
  };

  onSubmit = (): void => {
    this.errorStateMatcher.submitAttempted = true;
    if (this.saving || this.form.invalid) {
      return;
    }
    const formValue = this.form.value as ChangeBannerObject;
    const addonId = formValue.addonId?.trim();
    if (!addonId) {
      return;
    }
    this.saving = true;
    this.store.dispatch(new ChangeBannerAction({ ...formValue, addonId }));
    this.dialogRef.close(true);
  };

  cancel = (): void => {
    this.fillInitialForm();
    this.dialogRef.close();
  };

  fileChange = (file: Image): void => {
    this.file = file.file;
    this.previewUrl = file.previewUrl ?? null;
    const formValue = this.form.value as {
      originalImageWidth?: number;
      originalImageHeight?: number;
    };
    const originalWidth = file.width || formValue.originalImageWidth || 0;
    const originalHeight = file.height || formValue.originalImageHeight || 0;

    const previewWidth = Math.min(originalWidth, this.MAX_PREVIEW_WIDTH);
    const previewHeight =
      originalWidth > 0 ? (previewWidth / originalWidth) * originalHeight : 0;

    this.form.patchValue({
      file: file.file,
      originalImageWidth: String(originalWidth),
      originalImageHeight: String(originalHeight),
      imageWidth: String(previewWidth),
      imageHeight: String(previewHeight),
    });
  };

  removeFile = (): void => {
    this.file = null;
    this.form.patchValue({ file: "" });
    this.previewUrl = this.data?.banner?.imageUrl || null;
  };
}
